import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./notifications.module.scss";
import { NotificationItem, resolveNotificationTarget } from "../hooks/useNotifications";
import { stripMentionMarkers } from "../utils/mentionUtils";
import { onActivate } from "../utils/a11y";
import { ReactComponent as TasksIcon } from "../assets/icons/tasks.svg";
import { ReactComponent as MissionsIcon } from "../assets/icons/missions.svg";
import { ReactComponent as LeadsIcon } from "../assets/icons/leads.svg";
import { ReactComponent as InvoicesIcon } from "../assets/icons/invoices.svg";
import { ReactComponent as MeetingsIcon } from "../assets/icons/meetings.svg";
import { ReactComponent as RequestsIcon } from "../assets/icons/requests.svg";
import { ReactComponent as PeopleIcon } from "../assets/icons/people.svg";

interface NotificationsProps {
    notifications: NotificationItem[];
    unreadCount: number;
    isLoading: boolean;
    hasMore: boolean;
    onClose: (event: React.MouseEvent<any>) => void;
    onMarkAsRead: (notificationId: number) => void;
    onMarkAllAsRead: () => void;
    onDismiss: (notificationId: number) => void;
    onLoadMore: () => void;
}

const getCategoryIcon = (category: string) => {
    switch (category) {
        case "task": return <TasksIcon />;
        case "mission": return <MissionsIcon />;
        case "lead": return <LeadsIcon />;
        case "finance": return <InvoicesIcon />;
        case "collaboration": return <MeetingsIcon />;
        case "hr": return <PeopleIcon />;
        default: return <RequestsIcon />;
    }
};

const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
};

const Notifications: React.FC<NotificationsProps> = ({
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    onClose,
    onMarkAsRead,
    onMarkAllAsRead,
    onDismiss,
    onLoadMore,
}) => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<"all" | "unread">("all");

    const handleNotificationClick = (notification: NotificationItem) => {
        if (!notification.isRead) {
            onMarkAsRead(notification.notificationId);
        }
        // Go through the shared resolver, exactly as the inbox does. Navigating
        // to the raw actionUrl skipped the per-type adjustments — an incident or
        // panic notification lost its `?legDrawer=incidents` suffix and dropped
        // the dispatcher on the mission with the relevant drawer closed.
        const target = resolveNotificationTarget(notification);
        if (target) {
            navigate(target);
        }
    };

    const filteredNotifications = filter === "unread"
        ? notifications.filter((n) => !n.isRead)
        : notifications;

    return (
        <div className={classes.notificationContainer}>
            {/* Header */}
            <div className={classes.notificationHeader}>
                <div className={classes.headerLeft}>
                    <span className={classes.headerTitle}>Notifications</span>
                    {unreadCount > 0 && (
                        <span className={classes.unreadBadge}>{unreadCount}</span>
                    )}
                </div>
                <button type="button" className={`btn-unstyled ${classes.closeButton} bi bi-x-lg`} onClick={onClose} aria-label="Close notifications" />
            </div>

            {/* Toolbar */}
            <div className={classes.toolbar}>
                <div className={classes.filterTabs}>
                    <button
                        type="button"
                        className={`btn-unstyled ${classes.filterTab} ${filter === "all" ? classes.filterTabActive : ""}`}
                        onClick={() => setFilter("all")}
                    >
                        All
                    </button>
                    <button
                        type="button"
                        className={`btn-unstyled ${classes.filterTab} ${filter === "unread" ? classes.filterTabActive : ""}`}
                        onClick={() => setFilter("unread")}
                    >
                        Unread
                    </button>
                </div>
                <div className={classes.toolbarActions}>
                    {unreadCount > 0 && (
                        <button
                            type="button"
                            className={`btn-unstyled ${classes.toolbarLink}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onMarkAllAsRead();
                            }}
                        >
                            Mark all as read
                        </button>
                    )}
                </div>
            </div>

            {/* List */}
            <div className={classes.notificationList}>
                {filteredNotifications.length === 0 && !isLoading && (
                    <div className={classes.emptyState}>
                        <i className="bi bi-bell-slash" />
                        <span>{filter === "unread" ? "No unread notifications" : "No notifications yet"}</span>
                    </div>
                )}
                {filteredNotifications.map((notification) => (
                    <div
                        key={notification.notificationId}
                        className={`${classes.notificationItem} ${!notification.isRead ? classes.unread : ""}`}
                        {...onActivate(() => handleNotificationClick(notification))}
                    >
                        <div className={classes.notificationIcon}>
                            {getCategoryIcon(notification.category)}
                        </div>
                        <div className={classes.notificationContent}>
                            <span className={classes.notificationTitle}>
                                {notification.title}
                            </span>
                            {notification.message && (
                                <span className={classes.notificationMessage}>
                                    {stripMentionMarkers(notification.message)}
                                </span>
                            )}
                            <span className={classes.notificationDate}>
                                {formatDate(notification.createdAt)}
                            </span>
                        </div>
                        <div className={classes.notificationActions}>
                            {!notification.isRead && (
                                <button
                                    type="button"
                                    className={`btn-unstyled bi bi-check2 ${classes.actionIcon}`}
                                    title="Mark as read"
                                    aria-label="Mark as read"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onMarkAsRead(notification.notificationId);
                                    }}
                                />
                            )}
                            <button
                                type="button"
                                className={`btn-unstyled bi bi-x-lg ${classes.actionIcon}`}
                                title="Dismiss notification"
                                aria-label="Dismiss notification"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDismiss(notification.notificationId);
                                }}
                            />
                        </div>
                    </div>
                ))}
                {hasMore && filter === "all" && (
                    <div className={classes.loadMore} {...onActivate(onLoadMore)}>
                        {isLoading ? "Loading..." : "Load more"}
                    </div>
                )}
            </div>

            {/* The bell only ever holds the newest few. Anything older — or anything
                you need to search for — lives in the inbox. */}
            <div
                className={classes.loadMore}
                {...onActivate((e) => {
                    onClose(e as React.MouseEvent<any>);
                    navigate("/notifications");
                })}
            >
                View all notifications
            </div>
        </div>
    );
};

export default Notifications;
